import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PiMagnifyingGlass, PiQrCode } from 'react-icons/pi'
import RdaDrawer from '@/custolink-views/asset_frontend/redesigns/RdaDrawer'
import useUiContext from '@/custolink-hooks/useUiContext'
import { FrontendAssetScanApi } from '@/custolink-transpiled_by_server/bc.assets.api_methods'
import { useMutation } from '@tanstack/react-query'
import AssetDrawerScope from './AssetDrawerScope'
import { ErrorNote, PrimaryButton } from './formKit'

/**
 * "Which asset do you mean?" - opened from the home when a component needs an
 * asset. Same server lookup, same navigation and the same copy as before, in
 * the page design: the reason as a quiet lead, a rounded serial field with an
 * accent Search button, then "or" and the Scan key. Enter submits, and a
 * lookup that finds nothing now says so.
 *
 * The Scan key opens the shared Scan drawer (`onScan`, see HomeOverlays.tsx)
 * rather than navigating - there is no `/camera` route.
 */
export default function SelectAssetDrawer(p: {
  open: boolean
  close: () => void
  accent: string
  onScan: () => void
}) {
  const uic = useUiContext()
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  const lf = uic.organisationContext.lookAndFeel

  const q = useMutation({
    mutationFn: (query: string) => FrontendAssetScanApi.assetLookup(uic._orgId, query),
    mutationKey: ['SelectAssetDrawer_serialNumberLookup'],
  })

  useEffect(() => {
    if (q.data != null && q.data?.defaultViewResult != null) {
      navigate('/asset/' + q.data.defaultViewResult.serialnumber)
    } else if (q.data != null && q.data?.customViewResult != null) {
      navigate('/view/' + q.data.customViewResult.urlSlug + '/' + q.data.customViewResult.attrValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data])

  const notFound =
    q.isSuccess && q.data?.defaultViewResult == null && q.data?.customViewResult == null

  const placeholder =
    lf.i18nAssetFrontendSnoSearchbarPlaceholder != null
      ? t(lf.i18nAssetFrontendSnoSearchbarPlaceholder)
      : lf.assetFrontendSnoSearchbarPlaceholder

  return (
    <RdaDrawer
      title={t('assetSelection.title')}
      isOpen={p.open}
      placement="right"
      width={'100%'}
      onClose={p.close}
      onRequestClose={p.close}
    >
      <AssetDrawerScope accent={p.accent}>
        <div className="mx-auto flex w-full max-w-xl flex-col gap-5">
          <p className="text-sm leading-relaxed text-[var(--rda-muted)] sm:text-[15px]">
            {t('assetSelection.requiresAsset')}
          </p>

          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault()
              if (query.trim().length > 0) q.mutate(query.trim())
            }}
          >
            <label className="flex items-center gap-2 rounded-lg sm:rounded-xl border border-[color:var(--rda-line)] bg-[var(--rda-surface)] px-3 focus-within:border-[color:var(--rda-accent-text)] focus-within:ring-1 focus-within:ring-[color:var(--rda-accent-text)]">
              <PiMagnifyingGlass aria-hidden className="h-5 w-5 shrink-0 text-[var(--rda-muted)]" />
              <input
                value={query}
                placeholder={placeholder}
                aria-label={placeholder}
                className="w-full min-w-0 bg-transparent py-3 text-base outline-none placeholder:text-[color:var(--rda-muted)]"
                onChange={(ev) => {
                  setQuery(ev.target.value)
                  // a stale "no results" must not linger while a new serial is typed
                  if (q.isSuccess) q.reset()
                }}
              />
            </label>
            {notFound ? (
              <div className="overflow-hidden rounded-lg sm:rounded-xl">
                <ErrorNote>{t('nudge.search.noResults')}</ErrorNote>
              </div>
            ) : null}
            <PrimaryButton
              className="rounded-lg sm:rounded-xl"
              disabled={q.isPending || query.trim().length === 0}
              type="submit"
            >
              {q.isPending ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <PiMagnifyingGlass aria-hidden className="h-5 w-5" />
              )}
              {t('assetSelection.search')}
            </PrimaryButton>
          </form>

          <div aria-hidden className="flex items-center gap-3 text-[13px] font-medium lowercase text-[var(--rda-muted)]">
            <span className="h-px flex-1 bg-[var(--rda-hairline)]" />
            {t('assetSelection.or')}
            <span className="h-px flex-1 bg-[var(--rda-hairline)]" />
          </div>

          <button
            type="button"
            className="rda-tile flex w-full items-center justify-center gap-2 rounded-lg sm:rounded-xl border border-[color:var(--rda-line)] bg-[var(--rda-surface)] px-4 py-3 text-sm font-semibold text-[var(--rda-on-surface)]"
            onClick={p.onScan}
          >
            <PiQrCode aria-hidden className="h-5 w-5" />
            {t('assetSelection.scan')}
          </button>
        </div>
      </AssetDrawerScope>
    </RdaDrawer>
  )
}
